import { Formik } from 'formik';
import { useCallback, useMemo } from 'react';

import { Box, Divider, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useCurrentuser } from '@uplift/contexts';
import { useIsMobile, useL10n } from '@uplift/hooks';
import {
  useProviderLicensesStatusQuery, useProviderStatesSelectionMutation, useStates
} from '@uplift/queries';
import { State } from '@uplift/types';
import {
  ButtonStyleCheckbox, ButtonStyleCheckboxGroup, CancelButton, FormModal, ModalActions,
  ModalContent, ModalTitle, SubmitButton
} from '@uplift/ui-v2';

import { useOnboardingEventActions } from '../useOnboardingEventActions.hook';

export type SelectStatesModalProps = {
  closeModal: () => void;
};

export const SelectStatesModal: React.VFC<SelectStatesModalProps> = ({ closeModal }) => {
  const isMobile = useIsMobile();
  const { classes } = useStyles();
  const l10n = useL10n();

  const me = useCurrentuser();
  const states = useStates();
  const { data: licenses, isLoading } = useProviderLicensesStatusQuery(me?.id);
  const { stateSelectionSubmitted } = useOnboardingEventActions(me?.id);
  const providerStatesSelectionMutation = useProviderStatesSelectionMutation(me?.id);
  const licensedStatesIds = licenses?.map(license => license.stateId);
  const licensedStates = states.states.filter(state => licensedStatesIds?.includes(state.id));
  const liveStates = useMemo(() => licenses?.filter(license => license.live).map(license => license.stateId) || [], [ licenses ]);
  const isOneLicense = licenses?.length === 1;

  // If there is only one license, have license selected by default
  if (isOneLicense && liveStates.length === 0) {
    liveStates.push(licenses[0].stateId);
  }

  const handleSubmit = useCallback(async (values) => {
    await providerStatesSelectionMutation.mutateAsync({ stateIds: liveStates, setLive: false });
    await providerStatesSelectionMutation.mutateAsync({ stateIds: values.stateSelection, setLive: true });
    stateSelectionSubmitted();
    closeModal();
  }, [ closeModal, liveStates, providerStatesSelectionMutation, stateSelectionSubmitted ]);

  const initialValues = {
    stateSelection: liveStates
  };

  if (isLoading) {
    return null;
  }

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {formik => (
        <FormModal
          open
          showCloseIcon={false}
          classes={{ paperWidthXl: !isMobile && classes.paperWidthXl }}
          maxWidth={isMobile ? null : 'xl'}
          fullScreen={isMobile}
        >
          <ModalTitle classes={{ title: classes.titleBgColor, root: classes.root }}>
            <Box display="flex" p={1}>
              <Box className={classes.modalTitle}>
                {l10n.providerSettings.onboarding.gettingStarted}
              </Box>
            </Box>
          </ModalTitle>
          <Divider />
          {licensedStates.length > 0 && (
            <ModalContent noPadding>
              <Box className={classes.modalContent}>
                <Typography variant="h4">{isOneLicense ?
                  'Confirm the state you want to add to your UpLift practice' :
                  'Select the states you want to add to your UpLift practice'}
                </Typography>
                <Typography variant="body1" className={classes.subTitle}>Are you licensed in a state that isn’t listed? Let us know at help@joinuplift.co</Typography>
                <ButtonStyleCheckboxGroup name="stateSelection" color="secondary">
                  {licensedStates.map((licensedState: State) => (
                    <ButtonStyleCheckbox
                      key={licensedState.id}
                      label={licensedState.fullName}
                      value={licensedState.id}
                      checked={formik.values.stateSelection?.includes(licensedState.id)}
                    />
                  ))}
                </ButtonStyleCheckboxGroup>
              </Box>
            </ModalContent>
          )}
          <>
            <Divider className={classes.divider} />
            <ModalActions classes={{ root: classes.modalActionsRoot }}>
              <CancelButton onClick={closeModal} fullWidth={isMobile} variant={isMobile ? 'outlined' : 'contained'}>
                Cancel
              </CancelButton>
              <SubmitButton color="secondary" disabled={formik.isSubmitting || !formik.values.stateSelection?.length} fullWidth={isMobile}>
                Submit
              </SubmitButton>
            </ModalActions>
          </>
        </FormModal>
      )}
    </Formik>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: 0,
  },
  modalTitle: {
    display: 'flex',
    flexGrow: 1,
    color: theme.palette.darkSlate.main,
    fontSize: '1rem',
    alignItems: 'center',
    marginLeft: theme.spacing(2),
    fontWeight: 600,
    letterSpacing: '0.5px',
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.125rem',
      letterSpacing: '0.15px',
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '1.125rem',
      justifyContent: 'center',
    },
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    padding: '24px 16px 24px 16px',
    width: 'auto',
    paddingLeft: '16px',
    [theme.breakpoints.up('md')]: {
      width: '588px',
      paddingLeft: '226px',
      paddingTop: '30px',
      '& fieldset': {
        width: '343px',
      }
    },
  },
  titleBgColor: {
    backgroundColor: theme.palette.grey[100],
  },
  paperWidthXl: {
    width: '1040px',
    height: '680px',
  },
  divider: {
    display: 'flex',
    width: '100%',
  },
  modalActionsRoot: {
    [theme.breakpoints.up('md')]: {
      marginRight: '226px',
    },
  },
  subTitle: {
    fontSize: '16px',
    color: theme.palette.text.secondary,
  }
}));
